import React, { useState } from 'react';
import PDFViewer from './PDFViewer';
import ImageViewer from './ImageViewer';

const FileViewer = ({ file, filename, onBack }) => {
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const lowerName = (filename || '').toLowerCase();
  const isPDF = lowerName.endsWith('.pdf') || (typeof file === 'string' && (file.startsWith('JVBERi0') || file.startsWith('data:application/pdf')));

  const handlePDFPreview = () => {
    setShowPDFViewer(true);
  };

  const handleClosePDFViewer = () => {
    setShowPDFViewer(false);
  };

  const getPDFUrl = () => {
    if (typeof file === 'string') {
      // If already a data URL, return as-is
      if (file.startsWith('data:application/pdf')) return file;
      // If it's raw base64 content (no data URL prefix), convert to blob URL
      const byteCharacters = atob(file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    }
    // If file is already a blob/object URL
    return file;
  };

  const resolveImageSrc = () => {
    // If file is a data URL or an object URL, use directly
    if (typeof file === 'string' && file.startsWith('data:')) return file;
    if (typeof file === 'string' && file.startsWith('blob:')) return file;

    // Otherwise, it's a raw base64 payload; pick mime from filename
    let mime = 'image/png';
    if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) mime = 'image/jpeg';
    else if (lowerName.endsWith('.gif')) mime = 'image/gif';
    else if (lowerName.endsWith('.png')) mime = 'image/png';

    return `data:${mime};base64,${file}`;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="flex items-center text-indigo-600 hover:text-indigo-800"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Details
        </button>
        <h4 className="text-lg font-semibold text-gray-900">{filename}</h4>
      </div>

      <div className="border rounded-lg p-4 bg-gray-50">
        {isPDF ? (
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">PDF Document</p>
            <div className="space-x-3">
              <button
                onClick={handlePDFPreview}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View PDF
              </button>
              <a
                href={getPDFUrl()}
                download={filename}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download PDF
              </a>
            </div>
          </div>
        ) : (
          <div className="rounded-lg shadow-md overflow-hidden bg-gray-100 border border-gray-200" style={{ height: '600px' }}>
            <ImageViewer src={resolveImageSrc()} alt={filename} fileName={filename} />
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {isPDF && (
        <PDFViewer
          isOpen={showPDFViewer}
          onClose={handleClosePDFViewer}
          pdfUrl={getPDFUrl()}
          title={filename}
        />
      )}
    </div>
  );
};

export default FileViewer;